import { useMemo, useCallback } from 'react';
import { useMockServerStore } from '../../store/mockServerStore';
import { useCloudMockStore } from '../../store/cloudMockStore';
import { buildMockUrl, mockPathFromUrl } from '../../services/mockServerService';

/** Mock server URLs and the request's saved response examples (add, update, remove, duplicate, enable). */
export function useMockExamples({
  url,
  initialData,
  setResponseExamples,
  notifyChange,
}) {
  // ---- Local Mock: response example editing -----------------------------
  const mockServerRunning = useMockServerStore((s) => s.running);
  const mockServerBaseUrl = useMockServerStore((s) => s.baseUrl);
  const mockUrl = useMemo(
    () =>
      buildMockUrl(
        mockServerBaseUrl,
        initialData?.collectionId,
        mockPathFromUrl(url)
      ),
    [mockServerBaseUrl, initialData?.collectionId, url]
  );

  const cloudMockEnabled = useCloudMockStore((s) => s.enabled);
  // Use the store's baseUrl, which prefers the one the SERVER reported — only
  // the serving node knows which mock host it answers on. Rebuilding it here
  // from REACT_APP_CLOUD_MOCK_BASE_URL is what produced a localhost URL in
  // production builds.
  const cloudMockBaseUrl = useCloudMockStore((s) => s.baseUrl);
  const cloudMockUrl = useMemo(() => {
    if (!cloudMockBaseUrl || !initialData?.collectionId) return '';
    return buildMockUrl(cloudMockBaseUrl, initialData.collectionId, mockPathFromUrl(url));
  }, [cloudMockBaseUrl, initialData?.collectionId, url]);

  const addMockExample = useCallback(() => {
    setResponseExamples((prev) => {
      const next = [
        ...prev,
        {
          id: `ex-${Date.now()}`,
          name: '',
          status: 200,
          contentType: 'application/json',
          headers: [],
          body: '',
          matchQuery: [],
          matchHeaders: [],
          enabled: true,
          sourceType: 'example',
          schemaId: null,
        },
      ];
      notifyChange({ mock: { responseExamples: next } });
      return next;
    });
  }, [notifyChange, setResponseExamples]);

  const updateMockExample = useCallback(
    (id, patch) => {
      setResponseExamples((prev) => {
        const next = prev.map((ex) => (ex.id === id ? { ...ex, ...patch } : ex));
        notifyChange({ mock: { responseExamples: next } });
        return next;
      });
    },
    [notifyChange, setResponseExamples]
  );

  const removeMockExample = useCallback(
    (id) => {
      setResponseExamples((prev) => {
        const next = prev.filter((ex) => ex.id !== id);
        notifyChange({ mock: { responseExamples: next } });
        return next;
      });
    },
    [notifyChange, setResponseExamples]
  );

  const duplicateMockExample = useCallback(
    (id) => {
      setResponseExamples((prev) => {
        const src = prev.find((ex) => ex.id === id);
        if (!src) return prev;
        const next = [
          ...prev,
          { ...src, id: `ex-${Date.now()}`, name: `${src.name || ''} copy`.trim() },
        ];
        notifyChange({ mock: { responseExamples: next } });
        return next;
      });
    },
    [notifyChange, setResponseExamples]
  );

  const toggleMockExampleEnabled = useCallback(
    (id) => {
      setResponseExamples((prev) => {
        const next = prev.map((ex) =>
          ex.id === id ? { ...ex, enabled: ex.enabled === false } : ex
        );
        notifyChange({ mock: { responseExamples: next } });
        return next;
      });
    },
    [notifyChange, setResponseExamples]
  );

  return {
    mockServerRunning,
    mockUrl,
    cloudMockEnabled,
    cloudMockUrl,
    addMockExample,
    updateMockExample,
    removeMockExample,
    duplicateMockExample,
    toggleMockExampleEnabled,
  };
}
